import { z } from 'zod'

const envSchema = z.object({
  VITE_API_BASE_URL: z
    .string()
    .min(1)
    .refine(
      (value) => (value.startsWith('/') ? !value.startsWith('//') : /^https?:\/\/\S+$/.test(value)),
      {
        error:
          'VITE_API_BASE_URL must be an absolute http(s) URL or a same-origin path like /api (never protocol-relative //host)',
      },
    ),

  VITE_API_PROXY_TARGET: z.string().optional(),

  VITE_AI_BASE_URL: z
    .string()
    .refine((value) => value === '' || value.startsWith('/') || /^https?:\/\/\S+$/.test(value), {
      error: 'VITE_AI_BASE_URL must be an absolute http(s) URL, a path, or blank',
    })
    .optional(),

  VITE_AI_PROXY_TARGET: z.string().optional(),
  VITE_COGNITO_USER_POOL_ID: z.string().min(1),
  VITE_COGNITO_CLIENT_ID: z.string().min(1),
  VITE_AWS_REGION: z.string().min(1),

  VITE_AUTH_COOKIE_DOMAIN: z.string().optional(),

  VITE_CHAT_WS_HOST: z
    .string()
    .refine((value) => value === '' || /^wss?:\/\/\S+$/.test(value), {
      error: 'VITE_CHAT_WS_HOST must be a complete ws(s):// URL, or blank to disable Ask AI',
    })
    .optional(),
})

export type Env = z.infer<typeof envSchema>

export class EnvConfigError extends Error {
  override readonly name = 'EnvConfigError'

  constructor(issues: string[]) {
    super(
      `Invalid build-time configuration:\n${issues.map((i) => `  - ${i}`).join('\n')}\n` +
        'See .env.example for the five variables this application requires.',
    )
  }
}

export function parseEnv(source: Record<string, unknown>): Env {
  const result = envSchema.safeParse(source)
  if (!result.success) {
    throw new EnvConfigError(
      result.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`),
    )
  }
  return result.data
}

let cached: Env | undefined

export function getEnv(): Env {
  cached ??= parseEnv(import.meta.env)
  return cached
}

export function resetEnvForTests(): void {
  cached = undefined
}
